import { parseConfig } from '@utilities/theme';
import { EVENTS, publish, subscribe, clearSticky } from '@utilities/events';
import { fetchVariantData as resolveVariant, prefetchVariantData, buildProductUrl } from '@utilities/product';

/**
 * ProductOverview — State Orchestrator
 *
 * Owns product data, variant state, URL management, and data fetching.
 * Delegates all DOM manipulation to child custom elements:
 *   - <product-option-selectors>  (options UI, swatches, availability, inventory alert)
 *   - <product-form>              (form submission, ATC button, sticky ATB, preorder)
 *   - <product-price>             (price display, sale/final-sale badges)
 *
 * Fully independent siblings (subscribe to events, no direct coupling):
 *   - <product-promotion>         (promotional messaging)
 *   - <payment-terms-container>   (Shopify payment terms)
 *   - <truefit-widget>            (TrueFit sizing recommendations)
 *
 * All inter-component communication uses the pub-sub system (@utilities/events).
 * See utilities-events.js for the subscribe/publish/clearSticky API.
 *
 * Published events (via publish()):
 *   - product-overview:initialized             { productOverview, product, variant }           sticky
 *   - product-overview:variant-change          { productOverview, variant }                    sticky
 *   - product-overview:no-variant              { productOverview }                              sticky
 *   - product-overview:swatch-change           { productOverview, variant, data, element }
 *
 * Subscribed events (via subscribe()):
 *   - product-option-selectors:option-change   { productOverview, optionValueIds }
 *   - product-option-selectors:swatch-change   { productOverview, swatch, swatchData, optionValueIds, activeProductUrl }
 *   - product-option-selectors:invalid-option  { productOverview, reason }
 */
if (!customElements.get('product-overview')) {
  class ProductOverview extends HTMLElement {

    constructor() {
      super();
      this.baseUrl = window.Shopify.routes.root + 'products/';
      this.elements = {};
      this.renderingMode = this.dataset.renderingMode || 'server-side';
      this.prefetchedSwatchIntents = new Set();
      this.variantChangeInProgress = false;
      this.pendingProductSelectResolve = null; // Open shopify:product:select promise

      this.boundHandleSwatchIntentPrefetch = this.handleSwatchIntentPrefetch.bind(this);
    }

    async connectedCallback() {
      await this.init();
    }

    // ─── Debug ──────────────────────────────────────────────
    allowDebug = () => this.classList.contains('debug');

    // ─── Initialisation ─────────────────────────────────────
    init = async () => {
      try {
        if (this.allowDebug()) console.log('product-overview.js: init()', this);

        this.product = await this.setProduct();

        if (!this.product) {
          if (this.allowDebug()) console.log('product-overview.js: init() - Product data is null');
          return;
        }

        if (this.allowDebug()) console.log('product-overview.js: product', this.product);

        this.noVariant = false;
        this.activeProductUrl = this.product?.url ?? window.location.pathname;
        this.variant = this.product?.selected_variant ?? {};

        if (this.allowDebug()) console.log('product-overview.js: variant', this.variant);

        this.cacheElements();
        this.subscribeToEvents();
        this.setInteractionPrefetchListeners();

        this.initializing = true;

        if (this.product?.available) {
          await this.updateState();
        }

        this.classList.add('product-overview--initialized');
        publish(EVENTS.PRODUCT_OVERVIEW_INITIALIZED, {
          productOverview: this,
          product: this.product,
          variant: this.variant,
        }, { sticky: true });

        this.initializing = false;
      } catch (error) {
        console.error('Error initializing component:', error, this);
      }
    };

    // ─── Product Data ───────────────────────────────────────
    setProduct = async () => {
      if (this.renderingMode !== 'client-side') return parseConfig(this);

      try {
        const fetchUrl = this.dataset.fetchUrl;
        if (this.allowDebug()) console.log('product-overview.js: setProduct() fetching:', fetchUrl);

        const response = await fetch(fetchUrl);
        if (!response.ok) {
          console.warn(`product-overview.js: setProduct() failed (${response.status}):`, fetchUrl);
          return null;
        }

        return await response.json();
      } catch (error) {
        console.error('product-overview.js: setProduct() error:', error.message, this);
        return null;
      }
    };

    // ─── Element Cache (minimal — only elements this orchestrator needs) ──
    cacheElements() {
      this.elements.productActions = this.querySelector('.product__form-actions');
      this.optionSelectorsComponent = this.querySelector('product-option-selectors');
    }

    // ─── Subscriptions ─────────────────────────────────────
    subscribeToEvents() {
      this.unsubscribers = [
        subscribe(EVENTS.PRODUCT_OPTION_SELECTORS_OPTION_CHANGE, this.handleOptionChange.bind(this), { productOverview: this }),
        subscribe(EVENTS.PRODUCT_OPTION_SELECTORS_SWATCH_CHANGE, this.handleSwatchChange.bind(this), { productOverview: this }),
        subscribe(EVENTS.PRODUCT_OPTION_SELECTORS_INVALID_OPTION, this.handleInvalidOption.bind(this), { productOverview: this }),
      ];
    }

    disconnectedCallback() {
      if (this.unsubscribers) {
        this.unsubscribers.forEach((unsub) => unsub());
        this.unsubscribers = [];
      }

      this.removeEventListener('mouseover', this.boundHandleSwatchIntentPrefetch);
      this.removeEventListener('focusin', this.boundHandleSwatchIntentPrefetch);
      this.removeEventListener('touchstart', this.boundHandleSwatchIntentPrefetch);

      this.prefetchedSwatchIntents.clear();
    }

    // ─── Child Component Event Handlers ─────────────────────

    /**
     * Handles standard option or delimited option changes from product-option-selectors.
     * Fetches updated product data and triggers a full state update.
     * Skipped during initialization — the server already rendered the correct variant.
     */
    async handleOptionChange(data) {
      if (data.productOverview !== this) return;
      if (this.initializing) return;
      await this.fetchAndUpdateProduct(data.optionValueIds ?? this.getSelectedOptionValueIds());
    }

    /**
     * Handles swatch changes from product-option-selectors.
     * Updates active product URL, fetches data, and publishes swatch-change.
     * Media updates are handled by product-media subscribing to swatch-change.
     * Skipped during initialization — the server already rendered the correct variant.
     */
    async handleSwatchChange(data) {
      if (data.productOverview !== this) return;
      if (this.initializing) return;

      this.selectedSwatchData = data.swatchData;
      this.selectedSwatchElement = data.swatch;

      if (data.activeProductUrl) {
        this.activeProductUrl = data.activeProductUrl;
      }

      const optionValueIds = this.getSelectedOptionValueIds();
      const activeUrl = this.getActiveProductUrl();

      // Fallback media URLs in case swatchData.media_gallery is missing
      const mediaUrl = buildProductUrl(activeUrl, optionValueIds, { view: 'product-media-json' });
      const mediaHtmlUrl = buildProductUrl(activeUrl, optionValueIds, { view: 'product-media' });

      // Publish immediately so product-media can render from the inline
      // media_gallery data (no network request needed in the common case).
      publish(EVENTS.PRODUCT_OVERVIEW_SWATCH_CHANGE, {
        productOverview: this,
        data: this.selectedSwatchData,
        element: this.selectedSwatchElement,
        variant: null,
        mediaUrl,
        mediaHtmlUrl,
      });

      await this.fetchAndUpdateProduct(optionValueIds);
    }

    /**
     * Handles invalid delimited option state from product-option-selectors.
     * Clears the variant and updates the URL.
     */
    handleInvalidOption(data) {
      if (data.productOverview !== this) return;
      this.variant = {};
      this.noVariant = true;
      this.variantChangeInProgress = false;
      this.#settleProductSelect(null);
      this.setUrl();
      clearSticky(EVENTS.PRODUCT_OVERVIEW_VARIANT_CHANGE);
      publish(EVENTS.PRODUCT_OVERVIEW_NO_VARIANT, {
        productOverview: this,
      }, { sticky: true });
    }

    // ─── Data Fetching ──────────────────────────────────────
    getActiveProductUrl = () => {
      return (
        this.activeProductUrl ??
        this.selectedSwatchData?.url ??
        this.product?.url ??
        window.location.pathname
      );
    };

    /**
     * Fetch variant data via the shared product utility.
     * @param {string[]} optionValueIds
     * @param {string|null} baseUrl
     * @returns {Promise<Object|null>}
     */
    async fetchVariantData(optionValueIds = [], baseUrl = null) {
      const activeBaseUrl = baseUrl || this.getActiveProductUrl();
      if (this.allowDebug()) {
        console.log('product-overview.js: fetchVariantData()', activeBaseUrl, optionValueIds);
      }
      return resolveVariant(activeBaseUrl, optionValueIds);
    }

    /**
     * Fetch variant data and merge dynamic fields into the full product object.
     * Static fields (title, handle, badges, swatches, etc.) persist from
     * the initial setProduct() load and don't change between option selections.
     */
    fetchProductData = async (optionValueIds = []) => {
      const baseUrl = this.getActiveProductUrl();
      const data = await this.fetchVariantData(optionValueIds, baseUrl);
      if (!data) return null;

      this.product.selected_variant = data.selected_variant;
      this.product.options = data.options;
      this.product.url = data.url ?? this.product.url;

      this.variant = data.selected_variant ?? {};
      this.activeProductUrl = data.url ?? this.activeProductUrl;
      return data;
    };

    async prefetchProductData(optionValueIds = [], baseUrl = null) {
      await prefetchVariantData(baseUrl || this.getActiveProductUrl(), optionValueIds);
    }

    setInteractionPrefetchListeners() {
      this.addEventListener('mouseover', this.boundHandleSwatchIntentPrefetch);
      this.addEventListener('focusin', this.boundHandleSwatchIntentPrefetch);
      this.addEventListener('touchstart', this.boundHandleSwatchIntentPrefetch);
    }

    getOptionValueIdsForSwatchIntent(swatchInput) {
      const optionGroups = Array.from(this.querySelectorAll('.js-product-radios')).sort((a, b) => {
        const aKey = parseInt((a.dataset.optionKey || 'option1').replace('option', ''), 10);
        const bKey = parseInt((b.dataset.optionKey || 'option1').replace('option', ''), 10);
        return aKey - bKey;
      });

      const selectedIds = [];
      for (const group of optionGroups) {
        let selectedInput;
        if (group.contains(swatchInput)) {
          selectedInput = swatchInput;
        } else {
          selectedInput = group.classList.contains('js-product-delimited-option')
            ? group.querySelector('.js-variant-option:checked')
            : group.querySelector('input[type="radio"]:checked');
        }

        const optionValueId = selectedInput?.dataset?.optionValueId;
        if (optionValueId) {
          selectedIds.push(String(optionValueId));
        }
      }

      return selectedIds;
    }

    handleSwatchIntentPrefetch(event) {
      const swatchInput = event.target?.closest('.js-product-swatches input.js-variant-option');
      if (!swatchInput || !this.product?.swatches?.length) return;

      const optionValueId = swatchInput.dataset.optionValueId;
      const handleizedColor = swatchInput.dataset.jsHandle;
      const matchingSwatch = this.product.swatches.find((swatch) => {
        if (optionValueId) {
          return String(swatch.option_value_id) === String(optionValueId);
        }
        return swatch.handle === handleizedColor;
      });
      if (!matchingSwatch) return;

      const intentKey = String(matchingSwatch.option_value_id || matchingSwatch.handle || '');
      if (!intentKey || this.prefetchedSwatchIntents.has(intentKey)) return;
      this.prefetchedSwatchIntents.add(intentKey);

      const optionValueIds = this.getOptionValueIdsForSwatchIntent(swatchInput);
      const activeProductUrl = matchingSwatch.url || this.getActiveProductUrl();
      this.prefetchProductData(optionValueIds, activeProductUrl);

      const productMedia = this.querySelector('product-media');
      if (productMedia?.prefetchMedia) {
        const mediaUrl = buildProductUrl(activeProductUrl, optionValueIds, { view: 'product-media-json' });
        const mediaHtmlUrl = buildProductUrl(activeProductUrl, optionValueIds, { view: 'product-media' });
        productMedia.prefetchMedia(mediaUrl, { mediaHtmlUrl });
      }
    }

    // ─── State Updates ──────────────────────────────────────

    /**
     * Common flow: fetch product data for given option value IDs,
     * then trigger a full state update cycle.
     */
    async fetchAndUpdateProduct(optionValueIds = []) {
      this.variantChangeInProgress = true;

      // Announce the pending selection before the fetch, so listeners can show a
      // loading state against the promise. Settled on every exit below.
      this.dispatchProductSelectEvent();

      const updatedProduct = await this.fetchProductData(optionValueIds);
      if (!updatedProduct) {
        this.variantChangeInProgress = false;
        this.#settleProductSelect(null);
        return;
      }
      await this.updateState();
    }

    /**
     * Dispatch Shopify's shopify:product:select standard event for an in-flight
     * option selection.
     *
     * Fires from fetchAndUpdateProduct rather than from updateState, because only
     * that path represents a customer choosing an option. updateState also runs on
     * initial load, which shopify:product:view already reports.
     *
     * The event's promise resolves once resolution finishes, with the variant or
     * null. Null is a legitimate outcome, not an error: isSelectionComplete
     * withholds a variant until every option type has a value, so a PDP landing
     * with Size unselected resolves null by design.
     * See: https://shopify.dev/docs/storefronts/themes/best-practices/standard-events
     * @private
     */
    dispatchProductSelectEvent() {
      // A selection supersedes any earlier pending one.
      this.#settleProductSelect(null);

      let resolve;
      const promise = new Promise((res) => {
        resolve = res;
      });
      this.pendingProductSelectResolve = resolve;

      Promise.all([
        import('@shopify/events'),
        import('@utilities/product'),
        import('@utilities/standard-events'),
      ])
        .then(([{ ProductSelectEvent }, { isVariantFullyAvailable }, { buildSelectedOptions, buildSelectedVariantPayload }]) => {
          const product = this.product;
          if (!product?.id) return;

          this.dispatchEvent(
            new ProductSelectEvent({
              selectedOptions: buildSelectedOptions(product, this.variant),
              product: {
                id: product.id,
                title: product.title || '',
                handle: product.handle || '',
              },
              promise: promise.then((variant) => ({
                variant: buildSelectedVariantPayload(variant, this.product, isVariantFullyAvailable),
              })),
            })
          );
        })
        .catch((error) => {
          if (this.allowDebug()) {
            console.warn('product-overview.js: failed to dispatch shopify:product:select', error);
          }
        });
    }

    /**
     * Resolve the pending shopify:product:select promise, if one is open.
     * @param {Object|null} variant - Resolved variant, or null when unresolved
     * @private
     */
    #settleProductSelect(variant) {
      const resolve = this.pendingProductSelectResolve;
      if (!resolve) return;
      this.pendingProductSelectResolve = null;
      resolve(variant);
    }

    /**
     * Core state update cycle.
     * Updates URL, then publishes events so all subscribers react.
     */
    updateState = async () => {
      if (this.allowDebug()) console.log('product-overview.js: updateState', this.variant);

      this.variant = this.product?.selected_variant ?? this.variant ?? {};

      // Gate variant resolution on a complete option selection. The PDP intentionally
      // lands with secondary options (e.g. Size) unselected, so even though Shopify may
      // resolve a variant for a partial selection, we treat the variant as unresolved
      // until the customer has chosen one value for every option type.
      if (!this.isSelectionComplete()) {
        if (this.allowDebug()) {
          console.log('product-overview.js: incomplete option selection — withholding variant');
        }
        this.variant = {};
      }

      this.setUrl();

      // No valid variant — publish no-variant event
      if (!this.variant || Object.keys(this.variant).length === 0) {
        if (this.allowDebug()) {
          console.log('product-overview.js: unable to find variant for current selection');
        }
        this.variantChangeInProgress = false;
        this.#settleProductSelect(null);
        clearSticky(EVENTS.PRODUCT_OVERVIEW_VARIANT_CHANGE);
        publish(EVENTS.PRODUCT_OVERVIEW_NO_VARIANT, {
          productOverview: this,
        }, { sticky: true });
        return;
      }

      // Publish variant-change — all subscribers react to this
      this.variantChangeInProgress = false;
      this.#settleProductSelect(this.variant);
      clearSticky(EVENTS.PRODUCT_OVERVIEW_NO_VARIANT);
      publish(EVENTS.PRODUCT_OVERVIEW_VARIANT_CHANGE, {
        productOverview: this,
        variant: this.variant,
      }, { sticky: true });
    };

    // ─── URL Management ─────────────────────────────────────
    setUrl = () => {
      const locationUrl = new URL(window.location.href);
      const params = locationUrl.searchParams;

      const hasValidVariant = this.variant && Object.keys(this.variant).length > 0;

      if (hasValidVariant && this.variant.id) {
        params.set('variant', this.variant.id);
        params.delete('option_values');
      } else {
        params.delete('variant');
        params.delete('option_values');
      }

      if (!hasValidVariant) {
        this.noVariant = true;
      } else {
        this.noVariant = false;
      }

      if (!window.location.href.includes(`/products/${this.product?.handle}`)) return;

      locationUrl.search = params.toString();
      history.replaceState({}, '', locationUrl.toString());
    };

    // ─── Public API ────────────────────────────────────────
    getProduct = () => this.product ?? {};
    getVariant = () => this.variant;

    /** Delegates to <product-price>.setPrice(). Called by cyo-product.js. */
    setPrice = (priceHtml) => {
      const priceComponent = this.querySelector('product-price');
      if (priceComponent?.setPrice) {
        priceComponent.setPrice(priceHtml);
      }
    };

    /** Delegates to <product-form>.collectLineItemProperties(). Called by cyo-product.js. */
    collectLineItemProperties = (container) => {
      const formComponent = this.querySelector('product-form');
      if (formComponent?.collectLineItemProperties) {
        return formComponent.collectLineItemProperties(container);
      }
      return {};
    };

    getMonogramAddToCartData = () => {
      const monogramComponent = this.querySelector('pdp-monogramming');
      if (monogramComponent?.getMonogramAddToCartData) {
        return monogramComponent.getMonogramAddToCartData();
      }
      return monogramComponent?.monogramAddToCartData || null;
    };

    /** Delegates to <product-option-selectors>.selectTrueFitSize(). Called by truefit-widget.js. */
    selectTrueFitSize = (sizes) => {
      if (this.optionSelectorsComponent?.selectTrueFitSize) {
        this.optionSelectorsComponent.selectTrueFitSize(sizes);
      }
    };

    /**
     * Whether the customer has chosen a value for every option type.
     *
     * Each option type renders one `.js-product-radios` group (swatches, standard
     * radios, or a delimited container). A selection is complete when every group
     * has a checked value contributing an option value ID. Products with no option
     * groups (single-variant products) are always considered complete.
     *
     * @returns {boolean}
     */
    isSelectionComplete = () => {
      const optionGroups = Array.from(this.querySelectorAll('.js-product-radios'));
      if (optionGroups.length === 0) return true;

      return optionGroups.every((group) => {
        const isDelimited = group.classList.contains('js-product-delimited-option');
        const selectedInput = isDelimited
          ? group.querySelector('.js-variant-option:checked')
          : group.querySelector('input[type="radio"]:checked');
        return Boolean(selectedInput?.dataset?.optionValueId);
      });
    };

    /**
     * Delegates to the option selectors component for selected option value IDs.
     * Falls back to querying the DOM directly if the component isn't ready yet.
     */
    getSelectedOptionValueIds = () => {
      if (this.optionSelectorsComponent?.getSelectedOptionValueIds) {
        return this.optionSelectorsComponent.getSelectedOptionValueIds();
      }
      // Fallback: query DOM directly
      const optionGroups = this.querySelectorAll('.js-product-radios');
      const sorted = Array.from(optionGroups).sort((a, b) => {
        const aKey = parseInt(a.dataset.optionKey.replace('option', ''), 10);
        const bKey = parseInt(b.dataset.optionKey.replace('option', ''), 10);
        return aKey - bKey;
      });
      const selectedIds = [];
      for (const group of sorted) {
        const isDelimited = group.classList.contains('js-product-delimited-option');
        const selectedInput = isDelimited
          ? group.querySelector('.js-variant-option:checked')
          : group.querySelector('input[type="radio"]:checked');
        const optionValueId = selectedInput?.dataset?.optionValueId;
        if (optionValueId) selectedIds.push(optionValueId);
      }
      return selectedIds;
    };
  }

  customElements.define('product-overview', ProductOverview);
}
